import React, { useCallback, useState } from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityInspector } from '../../context/EntityInspectorContext';
import { parseEntityId } from '../../lib/entityIds';
import { entityTypeColor, entityTypeIcon, entityTypeLabel } from '../../lib/entityMeta';
import { fetchEntityInspect, type EntityInspectorResponse } from '../../services/api';
import EntityPreviewCard from './EntityPreviewCard';

export type EntityRefVariant = 'chip' | 'inline' | 'text';

export interface EntityRefProps {
  entityId: string;
  projectId: string;
  label?: string;
  status?: string;
  variant?: EntityRefVariant;
  size?: 'small' | 'medium';
  /** When false, click calls onClick instead of opening inspector. */
  inspectOnClick?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  stopPropagation?: boolean;
  sx?: object;
}

const EntityRef: React.FC<EntityRefProps> = ({
  entityId,
  projectId,
  label,
  status,
  variant = 'chip',
  size = 'small',
  inspectOnClick = true,
  onClick,
  stopPropagation = false,
  sx,
}) => {
  const { openInspector, navigateInspector, open: inspectorOpen } = useEntityInspector();
  const queryClient = useQueryClient();
  const [hovering, setHovering] = useState(false);

  const parsed = parseEntityId(entityId);
  const type = parsed?.type ?? 'entity';
  const color = entityTypeColor(type);
  const displayLabel = label ?? parsed?.id ?? entityId;

  const prefetch = useCallback(() => {
    void queryClient.prefetchQuery({
      queryKey: ['entities', entityId, projectId],
      queryFn: () => fetchEntityInspect(entityId, projectId),
      staleTime: 60_000,
    });
  }, [queryClient, entityId, projectId]);

  const previewQuery = queryClient.getQueryState<EntityInspectorResponse>(['entities', entityId, projectId]);
  const previewData = previewQuery?.data;
  const previewLoading = hovering && previewQuery?.fetchStatus === 'fetching';
  const previewError = previewQuery?.status === 'error';

  const handleClick = (event: React.MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
    if (inspectOnClick) {
      if (inspectorOpen) {
        navigateInspector(entityId, { label, status, type });
      } else {
        openInspector(entityId, projectId, { label, status, type });
      }
    }
    onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick(event as unknown as React.MouseEvent);
    }
  };

  const tooltip = (
    <EntityPreviewCard
      entityId={entityId}
      label={label}
      status={status}
      data={previewData}
      loading={previewLoading && !previewData}
      error={previewError}
    />
  );

  const commonProps = {
    onMouseEnter: () => {
      setHovering(true);
      prefetch();
    },
    onMouseLeave: () => setHovering(false),
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': `${entityTypeLabel(type)}: ${displayLabel}`,
  };

  if (variant === 'chip') {
    return (
      <Tooltip title={tooltip} placement="top" enterDelay={400}>
        <Chip
          {...commonProps}
          size={size}
          icon={
            <Box component="span" sx={{ fontSize: size === 'small' ? 12 : 14, pl: 0.5 }}>
              {entityTypeIcon(type)}
            </Box>
          }
          label={displayLabel}
          clickable
          sx={{
            height: size === 'small' ? 26 : 32,
            borderColor: alpha(color, 0.35),
            '&:hover': { bgcolor: alpha(color, 0.08) },
            ...sx,
          }}
        />
      </Tooltip>
    );
  }

  if (variant === 'inline') {
    return (
      <Tooltip title={tooltip} placement="top" enterDelay={400}>
        <Box
          component="span"
          {...commonProps}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            color,
            borderBottom: `1px dashed ${alpha(color, 0.45)}`,
            '&:hover': { borderBottomStyle: 'solid', bgcolor: alpha(color, 0.06) },
            ...sx,
          }}
        >
          <Box component="span" aria-hidden sx={{ fontSize: 12 }}>
            {entityTypeIcon(type)}
          </Box>
          <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontSize: 'inherit' }}>
            {displayLabel}
          </Typography>
          {status && (
            <Typography component="span" variant="caption" color="text.secondary">
              · {status}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="top" enterDelay={400}>
      <Typography
        component="span"
        {...commonProps}
        variant="body2"
        sx={{
          cursor: 'pointer',
          fontWeight: 600,
          color,
          '&:hover': { textDecoration: 'underline' },
          ...sx,
        }}
      >
        {displayLabel}
      </Typography>
    </Tooltip>
  );
};

export default EntityRef;
